import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabase/supabase";
import { useAuth } from "./AuthContext";
import { maskBlockedMembers } from "../utils/blocking";
import { resolveLineupWeek } from "../utils/season";

const LeagueContext = createContext(null);

export function LeagueProvider({ children }) {
  const { leagueId } = useParams();
  const { currentUser, seasonConfig } = useAuth();
  const [leagueData, setLeagueData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // True once a load has finished without finding the league. RLS hides leagues
  // the user is not a member of, so "deleted" and "not yours" look identical
  // here — both mean this route is a dead end.
  const [unavailable, setUnavailable] = useState(false);
  // users.id values this user has blocked. Loaded here rather than in
  // AuthContext because every surface that renders another manager's team
  // name, avatar or smack talk reads its members from this context, so masking
  // once at the source covers all of them.
  const [blockedIds, setBlockedIds] = useState(() => new Set());

  // currentWeek comes from session-level seasonConfig in AuthContext — no extra DB query needed.
  //
  // This is the *lineup* week, so it is floored at 1 and never 0. Consumers
  // rely on that in two ways that both fail silently otherwise: truthiness
  // guards (`if (!currentWeek) return`, `leagueWeek && ...`), where 0 is falsy
  // and the effect simply never runs; and `week${currentWeek}` keys, where
  // "week0" matches nothing because rosters compile into week 1.
  //
  // The old fallback only floored non-numeric labels, so "Off-Season" became 1
  // but "0" stayed 0. Setting the opening weekend live took out the Scoreboard,
  // Teams and Lineup tabs with an empty console. The database already floors
  // the same way: GREATEST(v_raw_week::INT, 1) in compile_draft_rosters and
  // instant_add_team.
  //
  // Anything that must distinguish week 0 from week 1 reads seasonConfig
  // directly instead, as TeamsTab does for bidding eligibility and scheduleWeek.
  const currentWeek = resolveLineupWeek(seasonConfig?.currentWeek);

  const load = useCallback(async () => {
    if (!leagueId) return;

    // maybeSingle, not single: single() rejects with PGRST116 when the row is
    // hidden or absent, and the rejection used to surface as a raw 406 body.
    const [{ data: league }, { data: memberRows }, { data: blockRows }] = await Promise.all([
      supabase.from("leagues").select("*").eq("id", leagueId).maybeSingle(),
      supabase.from("league_members")
        .select("id, user_id, team_name, points, avatar_url, smack_talk, users(first_name, last_name)")
        .eq("league_id", leagueId),
      // RLS restricts this to the caller's own blocks, so no filter is needed
      // and none should be added: a filter here would be a second place for
      // the two to disagree.
      supabase.from("user_blocks").select("blocked_id"),
    ]);

    setLeagueData(league ?? null);
    setUnavailable(!league);

    const blocked = new Set((blockRows || []).map((b) => b.blocked_id));
    setBlockedIds(blocked);

    setMembers(maskBlockedMembers(
      (memberRows || []).map((m) => ({
        ...m,
        first_name: m.users?.first_name || "",
        last_name: m.users?.last_name || "",
      })),
      blocked,
    ));

    setLoading(false);
  }, [leagueId]);

  useEffect(() => {
    setLoading(true);
    setLeagueData(null);
    setMembers([]);
    setBlockedIds(new Set());
    setUnavailable(false);
    load();

    const channel = supabase
      .channel(`league-ctx-${leagueId}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "league_members",
        filter: `league_id=eq.${leagueId}`
      }, load)
      .on("postgres_changes", {
        event: "UPDATE", schema: "public", table: "leagues",
        filter: `id=eq.${leagueId}`
      }, (payload) => {
        setLeagueData(payload.new);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [leagueId, load]);

  // The public demo league (migration 078), seen by someone who is not in it.
  // They view it as the league admin, the App Review account, so the Lineup
  // tab and every "mine" highlight have a manager to show. Read-only: RLS gives
  // a visitor no write policy, and the screens check isDemo to hide the
  // controls that would only fail.
  const ownMember = currentUser ? members.find((m) => m.user_id === currentUser.id) : null;
  const isDemo = !!(leagueData?.is_demo && !ownMember);
  const viewAsMember = isDemo
    ? members.find((m) => m.user_id === leagueData.admin_id) ?? null
    : ownMember;

  const isAdmin = !isDemo && !!(currentUser && leagueData && currentUser.id === leagueData.admin_id);
  // Co-admins are the up-to-two members the admin has deputised to enter picks
  // in an offline draft. They get no other admin powers.
  const isCoAdmin = !isDemo && !!(currentUser && leagueData?.co_admin_ids?.includes(currentUser.id));
  const isDraftComplete = leagueData?.draft_complete || false;
  const currentUserId = isDemo ? (viewAsMember?.user_id ?? null) : (currentUser?.id || null);
  const currentMemberId = viewAsMember?.id ?? null;

  return (
    <LeagueContext.Provider value={{ leagueId, leagueData, members, blockedIds, refreshLeague: load, isAdmin, isCoAdmin, isDraftComplete, currentUserId, currentMemberId, currentWeek, loading, unavailable, isDemo }}>
      {children}
    </LeagueContext.Provider>
  );
}

export function useLeague() {
  return useContext(LeagueContext);
}
